import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import BulkEditDialog from "./BulkEditDialog";
import Button from "./Button";
import Checkbox from "./Checkbox";
import ConfirmDialog from "./ConfirmDialog";
import Pagination, { ALL_ROWS } from "./Pagination";
import SkeletonRows from "./SkeletonRows";
import SortableHeader from "./SortableHeader";
import Spinner from "./Spinner";

// Mapping kept literal so Tailwind's purge scanner picks each class up.
const HIDE_BELOW = {
    sm: "hidden sm:table-cell",
    md: "hidden md:table-cell",
    lg: "hidden lg:table-cell",
    xl: "hidden xl:table-cell",
};

const HEADER_CELL_BASE =
    "whitespace-nowrap border-b border-ink-200 bg-ink-50 px-6 py-2 first:pl-6 last:pr-6";
const CELL_BASE = "whitespace-nowrap px-6 py-3 text-sm text-ink-700 first:pl-6 last:pr-6";

const compareBy = (a, b, accessor) => {
    const av = accessor(a);
    const bv = accessor(b);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (av < bv) return -1;
    if (av > bv) return 1;
    return 0;
};

const stableSort = (rows, accessor, direction) => {
    const indexed = rows.map((row, idx) => ({ row, idx }));
    indexed.sort((a, b) => {
        const cmp = compareBy(a.row, b.row, accessor);
        if (cmp !== 0) return direction === "asc" ? cmp : -cmp;
        return a.idx - b.idx;
    });
    return indexed.map((entry) => entry.row);
};

/**
 * Color tones for filter chips. `active` is the filled state, `inactive`
 * is the outlined state. Class strings stay literal so Tailwind's purge
 * scanner picks each one up.
 */
const CHIP_TONES = {
    brand: {
        active: "bg-brand-600 text-white border-brand-600",
        inactive: "bg-white text-ink-600 border-ink-200 hover:bg-ink-50",
    },
    gray: {
        active: "bg-ink-600 text-white border-ink-600",
        inactive: "bg-white text-ink-700 border-ink-300 hover:bg-ink-50",
    },
    lightGray: {
        active: "bg-ink-300 text-ink-900 border-ink-300",
        inactive: "bg-white text-ink-500 border-ink-200 hover:bg-ink-50",
    },
    yellow: {
        active: "bg-warning-500 text-white border-warning-500",
        inactive: "bg-white text-warning-800 border-warning-300 hover:bg-warning-50",
    },
    orange: {
        active: "bg-orange-500 text-white border-orange-500",
        inactive: "bg-white text-orange-700 border-orange-300 hover:bg-orange-50",
    },
    blue: {
        active: "bg-info-500 text-white border-info-500",
        inactive: "bg-white text-info-700 border-info-300 hover:bg-info-50",
    },
    darkBlue: {
        active: "bg-brand-700 text-white border-brand-700",
        inactive: "bg-white text-brand-700 border-brand-300 hover:bg-brand-50",
    },
    green: {
        active: "bg-success-500 text-white border-success-500",
        inactive: "bg-white text-success-700 border-success-300 hover:bg-success-50",
    },
    red: {
        active: "bg-danger-500 text-white border-danger-500",
        inactive: "bg-white text-danger-700 border-danger-300 hover:bg-danger-50",
    },
    darkGreen: {
        active: "bg-success-700 text-white border-success-700",
        inactive: "bg-white text-success-700 border-success-400 hover:bg-success-50",
    },
    neutral: {
        active: "bg-ink-700 text-white border-ink-700",
        inactive: "bg-white text-ink-600 border-ink-200 hover:bg-ink-50",
    },
};

/**
 * Filter strip with an optional uppercase label above a horizontally
 * scrollable row of color-coded chips. Each option may declare its own
 * `tone` (see CHIP_TONES); falls back to `brand`.
 */
const FilterChipStrip = ({ filter }) => {
    const { label, options = [], value, onChange } = filter;

    return (
        <div>
            {label && (
                <p className="mb-2 text-xs font-medium uppercase tracking-wider text-ink-500">
                    {label}
                </p>
            )}
            <div role="tablist" className="-mx-1 flex snap-x flex-wrap gap-2 px-1">
                {options.map((option) => {
                    const active = option.value === value;
                    const tone = CHIP_TONES[option.tone] || CHIP_TONES.brand;
                    return (
                        <button
                            key={option.value}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            onClick={() => onChange && onChange(option.value)}
                            className={`inline-flex shrink-0 snap-start items-center rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                                active ? tone.active : tone.inactive
                            }`}
                        >
                            {option.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

const DataTable = ({
    rows,
    columns,
    rowKey = "_id",
    loading = false,
    emptyMessage = "No records to display.",
    selectable = false,
    defaultSort,
    onRowClick,
    rowAction,
    bulkActions = [],
    bulkEdit,
    topContent,
    filters = [],
    totalCount,
    page,
    rowsPerPage,
    rowsPerPageOptions,
    onPageChange,
    onRowsPerPageChange,
    paginated = false,
    defaultRowsPerPage,
    isRowHighlighted,
    maxBodyHeight = "60vh",
    className = "",
    onSortChange,
}) => {
    const [order, setOrder] = useState((defaultSort && defaultSort.direction) || "asc");
    const [orderBy, setOrderBy] = useState(
        (defaultSort && defaultSort.id) || (columns[0] && columns[0].id) || "",
    );
    const [selected, setSelected] = useState([]);
    const [feedback, setFeedback] = useState(null);
    const [hoveredKey, setHoveredKey] = useState(null);
    const [pendingAction, setPendingAction] = useState(null);
    const [bulkEditOpen, setBulkEditOpen] = useState(false);
    const [successModal, setSuccessModal] = useState(null);

    const flashFeedback = (message, tone) => {
        if (!message) return;
        setFeedback({ message, tone });
        setTimeout(() => setFeedback(null), 2500);
    };

    /* ------------------------- Pagination -------------------------
     * Two modes:
     *   - server-side: parent passes `totalCount` and slices `rows` itself.
     *   - client-side: parent sets `paginated`, DataTable manages page +
     *     rowsPerPage internally and slices `sortedRows`.
     * ------------------------------------------------------------- */
    const isServerSide = totalCount != null;
    const isClientSide = paginated && !isServerSide;

    const sortedRows = useMemo(() => {
        if (!orderBy) return rows;
        const column = columns.find((c) => c.id === orderBy);
        if (!column || !column.sortable) return rows;
        // Server is handling the sort — use rows as received.
        if (isServerSide && onSortChange && column.serverSortField !== null) return rows;
        const accessor = column.sortValue || ((row) => row[orderBy]);
        return stableSort(rows, accessor, order);
    }, [rows, columns, orderBy, order, isServerSide, onSortChange]);
    const effectiveRowsPerPageOptions = rowsPerPageOptions || [10, 25, 50, 100];

    const [clientPage, setClientPage] = useState(0);
    const [clientRowsPerPage, setClientRowsPerPage] = useState(
        defaultRowsPerPage || effectiveRowsPerPageOptions[0],
    );

    // Clamp client-side page when row count changes (filter/search updates).
    useEffect(() => {
        if (!isClientSide || clientRowsPerPage === ALL_ROWS) return;
        const maxPage = Math.max(0, Math.ceil(sortedRows.length / clientRowsPerPage) - 1);
        if (clientPage > maxPage) setClientPage(maxPage);
    }, [isClientSide, sortedRows.length, clientRowsPerPage, clientPage]);

    const visibleRows =
        isClientSide && clientRowsPerPage !== ALL_ROWS
            ? sortedRows.slice(clientPage * clientRowsPerPage, (clientPage + 1) * clientRowsPerPage)
            : sortedRows;

    const handleSort = (column) => {
        if (!column.sortable) return;
        const newDirection = orderBy === column.id ? (order === "asc" ? "desc" : "asc") : "asc";
        setOrderBy(column.id);
        setOrder(newDirection);
        // Delegate to server when in server-side mode and column supports it.
        // serverSortField === null means client-sort only (computed/joined fields).
        if (isServerSide && onSortChange && column.serverSortField !== null) {
            const field =
                typeof column.serverSortField === "string" ? column.serverSortField : column.id;
            onSortChange(field, newDirection);
        }
    };

    const allSelected = sortedRows.length > 0 && selected.length === sortedRows.length;
    const someSelected = selected.length > 0 && !allSelected;
    const selectionCount = selected.length;

    const toggleAll = () => {
        setSelected(allSelected ? [] : sortedRows.map((row) => row[rowKey]));
    };

    const toggleRow = (id) => {
        setSelected((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
    };

    const runBulkAction = (action) => {
        if (action.confirm !== false) {
            setPendingAction(action);
        } else {
            executeBulkAction(action);
        }
    };

    const executeBulkAction = async (action) => {
        setPendingAction(null);
        try {
            await action.onClick(selected);
            if (action.clearSelectionOnSuccess !== false) setSelected([]);
            if (action.successModal) {
                setSuccessModal({
                    title: action.successTitle || "Action complete",
                    message: action.successMessage || "Record(s) successfully updated",
                });
            } else {
                flashFeedback(action.successMessage, "success");
            }
        } catch (error) {
            flashFeedback(
                action.errorMessage || "Something went wrong. Please contact support.",
                "danger",
            );
        }
    };

    const handleBulkEditSubmit = async (values) => {
        await bulkEdit.onSubmit(selected, values);
        setBulkEditOpen(false);
        if (bulkEdit.clearSelectionOnSuccess !== false) setSelected([]);
        setSuccessModal({
            title: bulkEdit.successTitle || "Update complete",
            message: bulkEdit.successMessage || "Record(s) successfully updated",
        });
    };

    const renderHeaderCells = () =>
        columns.map((column) => {
            const alignClass = column.align === "right" ? "text-right" : "text-left";
            const hideClass = column.hideBelow ? HIDE_BELOW[column.hideBelow] : "";
            const widthClass = column.width || "";

            return (
                <th
                    key={column.id}
                    scope="col"
                    className={`${HEADER_CELL_BASE} ${alignClass} ${hideClass} ${widthClass} ${
                        column.headerClassName || ""
                    }`}
                    aria-sort={
                        orderBy === column.id
                            ? order === "asc"
                                ? "ascending"
                                : "descending"
                            : "none"
                    }
                >
                    {column.sortable ? (
                        <SortableHeader
                            active={orderBy === column.id}
                            direction={orderBy === column.id ? order : "asc"}
                            onClick={() => handleSort(column)}
                        >
                            {column.label}
                        </SortableHeader>
                    ) : (
                        <span className="text-xs font-medium text-ink-500">{column.label}</span>
                    )}
                </th>
            );
        });

    const renderRow = (row, index) => {
        const id = row[rowKey];
        const itemSelected = selected.includes(id);
        const highlighted = isRowHighlighted ? isRowHighlighted(row) : false;
        const clickable = Boolean(onRowClick);

        const isOdd = index % 2 !== 0;
        const isHovered = clickable && hoveredKey === id;
        const tdBg = isHovered
            ? itemSelected
                ? "bg-brand-100"
                : highlighted
                  ? "bg-warning-100"
                  : "bg-ink-100"
            : itemSelected
              ? "bg-brand-50"
              : highlighted
                ? "bg-warning-50"
                : isOdd
                  ? "bg-ink-50"
                  : "bg-white";
        const rowClasses = [
            "transition-colors",
            clickable ? "cursor-pointer" : "",
        ].join(" ");

        return (
            <tr
                key={id != null ? id : index}
                onClick={clickable ? () => onRowClick(row) : undefined}
                onMouseEnter={clickable ? () => setHoveredKey(id) : undefined}
                onMouseLeave={clickable ? () => setHoveredKey(null) : undefined}
                className={rowClasses}
            >
                {selectable && (
                    <td className={`${CELL_BASE} ${tdBg} w-10`}>
                        <Checkbox
                            checked={itemSelected}
                            onChange={(e) => {
                                e.stopPropagation();
                                toggleRow(id);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            ariaLabel={`Select row ${index + 1}`}
                        />
                    </td>
                )}
                {columns.map((column) => {
                    const alignClass = column.align === "right" ? "text-right" : "text-left";
                    const hideClass = column.hideBelow ? HIDE_BELOW[column.hideBelow] : "";
                    const widthClass = column.width || "";
                    const content = column.cell ? column.cell(row, index) : row[column.id];
                    return (
                        <td
                            key={column.id}
                            className={`${CELL_BASE} ${tdBg} ${alignClass} ${hideClass} ${widthClass} ${
                                column.cellClassName || ""
                            }`}
                        >
                            {content == null || content === "" ? "—" : content}
                        </td>
                    );
                })}
                {rowAction && (
                    <td className={`${CELL_BASE} ${tdBg} w-12 text-right`}>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                rowAction.onClick(row);
                            }}
                            disabled={rowAction.disabled ? rowAction.disabled(row) : false}
                            aria-label={
                                typeof rowAction.ariaLabel === "function"
                                    ? rowAction.ariaLabel(row)
                                    : rowAction.ariaLabel || "Row action"
                            }
                            className="rounded-lg py-0.5 px-2 leading-none text-ink-400 transition-colors hover:bg-ink-100 hover:text-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {rowAction.icon}
                        </button>
                    </td>
                )}
            </tr>
        );
    };

    const totalCols = columns.length + (selectable ? 1 : 0) + (rowAction ? 1 : 0);

    const showPagination =
        isClientSide ||
        (page != null &&
            rowsPerPage != null &&
            totalCount != null &&
            onPageChange &&
            onRowsPerPageChange);

    return (
        <div
            className={`overflow-hidden rounded-2xl border border-ink-100 bg-white shadow-card ${className}`}
        >
            {feedback && (
                <div
                    role="status"
                    className={`flex items-center justify-between border-b px-4 py-2 text-sm sm:px-6 ${
                        feedback.tone === "success"
                            ? "border-success-200 bg-success-50 text-success-700"
                            : "border-danger-200 bg-danger-50 text-danger-700"
                    }`}
                >
                    <span>{feedback.message}</span>
                    <button
                        type="button"
                        onClick={() => setFeedback(null)}
                        aria-label="Dismiss"
                        className="opacity-70 hover:opacity-100"
                    >
                        <i className="fas fa-times" aria-hidden="true" />
                    </button>
                </div>
            )}

            {topContent && <div className="bg-white px-4 py-3 sm:px-6">{topContent}</div>}

            {filters.length > 0 && (
                <div className="space-y-3 border-b border-ink-100 bg-white px-4 py-3 sm:px-6">
                    {filters.map((filter) => (
                        <FilterChipStrip key={filter.id} filter={filter} />
                    ))}
                </div>
            )}

            {selectionCount > 0 && (
                <div className="flex items-center justify-between border-b border-ink-100 bg-brand-50/60 px-4 py-3 transition-colors sm:px-6">
                    <p className="text-sm font-medium text-ink-700">{selectionCount} selected</p>
                    {(bulkActions.length > 0 || bulkEdit) && (
                        <div className="flex items-center gap-2">
                            {bulkEdit && (
                                <Button
                                    variant={bulkEdit.variant || "secondary"}
                                    size="sm"
                                    onClick={() => setBulkEditOpen(true)}
                                    leadingIcon={bulkEdit.icon}
                                >
                                    {bulkEdit.label || "Edit"}
                                </Button>
                            )}
                            {bulkActions.map((action) => (
                                <Button
                                    key={action.label}
                                    variant={action.variant || "secondary"}
                                    size="sm"
                                    onClick={() => runBulkAction(action)}
                                    leadingIcon={action.icon}
                                >
                                    {action.label}
                                </Button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <div className="relative overflow-auto" style={{ maxHeight: maxBodyHeight }}>
                {loading && sortedRows.length > 0 && (
                    <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/60">
                        <Spinner size="md" />
                    </div>
                )}
                <table className="w-full border-collapse">
                    <thead className="sticky top-0 z-10">
                        <tr>
                            {selectable && (
                                <th className={`${HEADER_CELL_BASE} w-10`}>
                                    <Checkbox
                                        checked={allSelected}
                                        indeterminate={someSelected}
                                        onChange={toggleAll}
                                        ariaLabel="Select all rows"
                                    />
                                </th>
                            )}
                            {renderHeaderCells()}
                            {rowAction && (
                                <th className={`${HEADER_CELL_BASE} w-12`} aria-label="Actions" />
                            )}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-ink-100">
                        {loading && sortedRows.length === 0 ? (
                            <SkeletonRows
                                columnCount={columns.length}
                                selectable={selectable}
                                hasRowAction={Boolean(rowAction)}
                            />
                        ) : (
                            <>
                                {visibleRows.map(renderRow)}
                                {sortedRows.length === 0 && !loading && (
                                    <tr>
                                        <td
                                            colSpan={totalCols}
                                            className="px-6 py-12 text-center text-sm text-ink-500"
                                        >
                                            {emptyMessage}
                                        </td>
                                    </tr>
                                )}
                            </>
                        )}
                    </tbody>
                </table>
            </div>

            {showPagination && (
                <Pagination
                    page={isClientSide ? clientPage : page}
                    rowsPerPage={isClientSide ? clientRowsPerPage : rowsPerPage}
                    count={isClientSide ? sortedRows.length : totalCount}
                    rowsPerPageOptions={effectiveRowsPerPageOptions}
                    onPageChange={isClientSide ? setClientPage : onPageChange}
                    onRowsPerPageChange={
                        isClientSide
                            ? (n) => {
                                  setClientRowsPerPage(n);
                                  setClientPage(0);
                              }
                            : onRowsPerPageChange
                    }
                />
            )}

            <ConfirmDialog
                open={Boolean(pendingAction)}
                title={pendingAction?.confirmTitle || "Are you sure?"}
                description={
                    pendingAction?.confirmDescription ||
                    `This will permanently delete ${selected.length} record${selected.length === 1 ? "" : "s"}. This cannot be undone.`
                }
                confirmLabel={pendingAction?.confirmLabel || "Delete"}
                confirmVariant={pendingAction?.confirmVariant || "danger"}
                onConfirm={() => executeBulkAction(pendingAction)}
                onCancel={() => setPendingAction(null)}
            />

            {bulkEdit && (
                <BulkEditDialog
                    open={bulkEditOpen}
                    fields={bulkEdit.fields}
                    onSubmit={handleBulkEditSubmit}
                    onCancel={() => setBulkEditOpen(false)}
                    title={bulkEdit.title || "Bulk edit"}
                    description={bulkEdit.description}
                    submitLabel={bulkEdit.submitLabel}
                    cancelLabel={bulkEdit.cancelLabel}
                    selectionCount={selectionCount}
                />
            )}

            <ConfirmDialog
                open={Boolean(successModal)}
                title={
                    <span className="flex items-center gap-2 text-success-700">
                        <i
                            className="fas fa-check-circle text-success-600"
                            aria-hidden="true"
                        />
                        {successModal?.title}
                    </span>
                }
                description={successModal?.message}
                confirmLabel="OK"
                confirmVariant="success"
                hideCancel
                onConfirm={() => setSuccessModal(null)}
                onCancel={() => setSuccessModal(null)}
            />
        </div>
    );
};

DataTable.propTypes = {
    rows: PropTypes.array.isRequired,
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.node,
            sortable: PropTypes.bool,
            sortValue: PropTypes.func,
            align: PropTypes.oneOf(["left", "right"]),
            hideBelow: PropTypes.oneOf(["sm", "md", "lg", "xl"]),
            width: PropTypes.string,
            headerClassName: PropTypes.string,
            cellClassName: PropTypes.string,
            cell: PropTypes.func,
        }),
    ).isRequired,
    rowKey: PropTypes.string,
    loading: PropTypes.bool,
    emptyMessage: PropTypes.node,
    selectable: PropTypes.bool,
    defaultSort: PropTypes.shape({
        id: PropTypes.string,
        direction: PropTypes.oneOf(["asc", "desc"]),
    }),
    onRowClick: PropTypes.func,
    rowAction: PropTypes.shape({
        icon: PropTypes.node.isRequired,
        onClick: PropTypes.func.isRequired,
        ariaLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
        disabled: PropTypes.func,
    }),
    bulkActions: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            onClick: PropTypes.func.isRequired,
            icon: PropTypes.node,
            variant: PropTypes.string,
            successMessage: PropTypes.string,
            successTitle: PropTypes.string,
            successModal: PropTypes.bool,
            errorMessage: PropTypes.string,
            clearSelectionOnSuccess: PropTypes.bool,
        }),
    ),
    bulkEdit: PropTypes.shape({
        label: PropTypes.string,
        icon: PropTypes.node,
        variant: PropTypes.string,
        title: PropTypes.string,
        description: PropTypes.node,
        submitLabel: PropTypes.string,
        cancelLabel: PropTypes.string,
        successTitle: PropTypes.string,
        successMessage: PropTypes.string,
        clearSelectionOnSuccess: PropTypes.bool,
        fields: PropTypes.array.isRequired,
        onSubmit: PropTypes.func.isRequired,
    }),
    topContent: PropTypes.node,
    filters: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.string,
            value: PropTypes.string,
            onChange: PropTypes.func.isRequired,
            options: PropTypes.arrayOf(
                PropTypes.shape({
                    value: PropTypes.string.isRequired,
                    label: PropTypes.string.isRequired,
                    tone: PropTypes.string,
                }),
            ).isRequired,
        }),
    ),
    totalCount: PropTypes.number,
    page: PropTypes.number,
    rowsPerPage: PropTypes.number,
    rowsPerPageOptions: PropTypes.arrayOf(
        PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.shape({
                value: PropTypes.number.isRequired,
                label: PropTypes.string.isRequired,
            }),
        ]),
    ),
    onPageChange: PropTypes.func,
    onRowsPerPageChange: PropTypes.func,
    paginated: PropTypes.bool,
    defaultRowsPerPage: PropTypes.number,
    isRowHighlighted: PropTypes.func,
    maxBodyHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    className: PropTypes.string,
};

export default DataTable;
